import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './AuthContext';

const CartContext = createContext();

const CART_STORAGE_KEY = 'ecommerce_cart_items';
const CART_COLLECTION = 'carts';

const normalizeItem = (item) => {
  if (!item || typeof item !== 'object' || !item.id) return null;

  const quantity = Number.parseInt(item.quantity, 10);

  return {
    ...item,
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1
  };
};

const normalizeItems = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map(normalizeItem)
    .filter(Boolean);

const toComparableShape = (items = []) =>
  normalizeItems(items)
    .map((item) => ({
      id: String(item.id),
      quantity: Number(item.quantity || 1),
      price: Number.parseFloat(item.price || 0),
      name: String(item.name || ''),
      image: String(item.image || item.imageUrl || '')
    }))
    .sort((a, b) => a.id.localeCompare(b.id));

const getItemsSignature = (items = []) => JSON.stringify(toComparableShape(items));

const areItemsEqual = (left = [], right = []) =>
  getItemsSignature(left) === getItemsSignature(right);

const saveToStorage = (items) => {
  try {
    const cartData = {
      items: normalizeItems(items),
      timestamp: Date.now(),
      version: '2.0'
    };

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    return true;
  } catch (error) {
    console.error('Error saving cart to storage:', error);
    return false;
  }
};

const loadFromStorage = () => {
  try {
    let savedData = localStorage.getItem(CART_STORAGE_KEY);
    if (!savedData) {
      savedData = sessionStorage.getItem(CART_STORAGE_KEY);
    }

    if (!savedData) return [];

    const parsed = JSON.parse(savedData);
    return normalizeItems(parsed?.items || []);
  } catch (error) {
    console.error('Error loading cart from storage:', error);
    return [];
  }
};

const mergeLocalAndCloudItems = (localItems = [], cloudItems = []) => {
  const mergedMap = new Map();

  normalizeItems(cloudItems).forEach((item) => {
    mergedMap.set(item.id, item);
  });

  normalizeItems(localItems).forEach((localItem) => {
    const existing = mergedMap.get(localItem.id);

    if (!existing) {
      mergedMap.set(localItem.id, localItem);
      return;
    }

    // Avoid doubling the same cart after re-login. Keep the higher quantity.
    mergedMap.set(localItem.id, {
      ...existing,
      ...localItem,
      quantity: Math.max(existing.quantity || 1, localItem.quantity || 1)
    });
  });

  return Array.from(mergedMap.values());
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_CART': {
      const nextItems = normalizeItems(action.payload || []);
      if (areItemsEqual(state.items, nextItems)) return state;
      return { ...state, items: nextItems };
    }

    case 'ADD_TO_CART': {
      const incoming = normalizeItem(action.payload);
      if (!incoming) return state;

      const qtyToAdd = Math.max(1, Number.parseInt(incoming.quantity, 10) || 1);
      const existingItem = state.items.find((item) => item.id === incoming.id);

      if (existingItem) {
        return {
          ...state,
          items: state.items.map((item) =>
            item.id === incoming.id
              ? { ...item, quantity: Math.max(1, Number(item.quantity || 1) + qtyToAdd) }
              : item
          )
        };
      }

      return {
        ...state,
        items: [...state.items, { ...incoming, quantity: qtyToAdd }]
      };
    }

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload)
      };

    case 'UPDATE_QUANTITY': {
      const nextQuantity = Number.parseInt(action.payload?.quantity, 10);
      if (!Number.isFinite(nextQuantity) || nextQuantity <= 0) {
        return {
          ...state,
          items: state.items.filter((item) => item.id !== action.payload?.id)
        };
      }

      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload?.id ? { ...item, quantity: nextQuantity } : item
        )
      };
    }

    case 'CLEAR_CART':
      if (!state.items.length) return state;
      return { ...state, items: [] };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  const isInitialized = useRef(false);
  const hasMergedLocalRef = useRef(false);
  const lastSyncedSignatureRef = useRef('');

  useEffect(() => {
    let unsubscribe;

    const applyLocalCart = () => {
      const localItems = loadFromStorage();
      dispatch({ type: 'LOAD_CART', payload: localItems });
      lastSyncedSignatureRef.current = getItemsSignature(localItems);
      isInitialized.current = true;
    };

    if (!user?.uid) {
      hasMergedLocalRef.current = false;
      applyLocalCart();
      return undefined;
    }

    hasMergedLocalRef.current = false;
    const userCartRef = doc(db, CART_COLLECTION, user.uid);

    unsubscribe = onSnapshot(
      userCartRef,
      async (snapshot) => {
        const cloudItems = normalizeItems(snapshot.data()?.items || []);

        if (!hasMergedLocalRef.current) {
          const localItems = loadFromStorage();
          const mergedItems = mergeLocalAndCloudItems(localItems, cloudItems);

          hasMergedLocalRef.current = true;
          dispatch({ type: 'LOAD_CART', payload: mergedItems });
          saveToStorage(mergedItems);
          lastSyncedSignatureRef.current = getItemsSignature(mergedItems);
          isInitialized.current = true;

          if (!areItemsEqual(mergedItems, cloudItems)) {
            try {
              await setDoc(
                userCartRef,
                {
                  userId: user.uid,
                  items: mergedItems,
                  updatedAt: serverTimestamp()
                },
                { merge: true }
              );
            } catch (error) {
              console.error('Error merging local cart into cloud cart:', error);
            }
          }

          return;
        }

        dispatch({ type: 'LOAD_CART', payload: cloudItems });
        saveToStorage(cloudItems);
        lastSyncedSignatureRef.current = getItemsSignature(cloudItems);
        isInitialized.current = true;
      },
      (error) => {
        console.error('Error syncing cloud cart:', error);
        applyLocalCart();
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!isInitialized.current) return;

    const normalizedItems = normalizeItems(state.items);
    saveToStorage(normalizedItems);

    const currentSignature = getItemsSignature(normalizedItems);
    if (currentSignature === lastSyncedSignatureRef.current) return;

    lastSyncedSignatureRef.current = currentSignature;

    if (!user?.uid) return;

    const userCartRef = doc(db, CART_COLLECTION, user.uid);
    setDoc(
      userCartRef,
      {
        userId: user.uid,
        items: normalizedItems,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    ).catch((error) => {
      console.error('Error saving cart to cloud:', error);
    });
  }, [state.items, user?.uid]);

  const addToCart = (product) => {
    dispatch({ type: 'ADD_TO_CART', payload: product });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    const parsedQuantity = Number.parseInt(quantity, 10);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
      return;
    }

    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity: parsedQuantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () =>
    state.items.reduce((sum, item) => {
      const price = Number.parseFloat(item.price) || 0;
      const quantity = Number.parseInt(item.quantity, 10) || 0;
      return sum + price * quantity;
    }, 0);

  const getCartItemsCount = () =>
    state.items.reduce((sum, item) => {
      const quantity = Number.parseInt(item.quantity, 10) || 0;
      return sum + quantity;
    }, 0);

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartItemsCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
